import React, { useState } from 'react';
import { Organization, UserProfile } from '../types';
import { Avatar, ImageUpload, PhoneInput } from './common';
import { EditProfileModal } from './modals';
import { sanitizeEntityName, validatePhoneNumber } from '../lib/helpers';
import { COUNTRIES_STATES } from '../lib/constants';

interface PendingOrgScreenProps {
  org: Organization;
  user: UserProfile;
  onUpdateOrg: (id: string, data: Partial<Organization>) => Promise<void>;
  onUpdateUser: (id: string, data: Partial<UserProfile>) => Promise<void>;
  onLogout: () => void;
}

export const PendingOrgScreen: React.FC<PendingOrgScreenProps> = ({
  org,
  user,
  onUpdateOrg,
  onUpdateUser,
  onLogout
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({
    name: org.name || '',
    phone: org.phone || '',
    type: org.type || 'school',
    photoUrl: org.photoUrl || '',
    address: {
      street: org.address?.street || '',
      city: org.address?.city || '',
      state: org.address?.state || '',
      country: org.address?.country || ''
    },
    verification: {
      affiliationNumber: org.verification?.affiliationNumber || '',
      publicUrl: org.verification?.publicUrl || '',
      documentUrl: org.verification?.documentUrl || '',
      documentName: org.verification?.documentName || '',
      notes: org.verification?.notes || ''
    }
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showDocModal, setShowDocModal] = useState(false);

  const status = org.status || 'pending';
  const revisionCount = org.verification?.revisionCount || 1;
  const isRevised = org.verification?.isRevisedAfterSubmission || revisionCount > 1;

  const handleDocUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrorMessage('File size must be under 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onload = (ev) => {
        setEditForm(prev => ({
          ...prev,
          verification: {
            ...prev.verification,
            documentUrl: ev.target?.result as string,
            documentName: file.name
          }
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveEdit = async () => {
    setErrorMessage('');
    if (!editForm.name.trim()) {
      setErrorMessage('Organization name is required.');
      return;
    }
    if (!editForm.phone || !validatePhoneNumber(editForm.phone)) {
      setErrorMessage('A valid organization contact phone number is required.');
      return;
    }
    if (!editForm.address.country || !editForm.address.state || !editForm.address.city) {
      setErrorMessage('City, State, and Country are required.');
      return;
    }

    setIsSaving(true);
    try {
      const newRevCount = (org.verification?.revisionCount || 1) + 1;
      const now = new Date();

      await onUpdateOrg(org.id, {
        name: sanitizeEntityName(editForm.name),
        phone: editForm.phone,
        type: editForm.type,
        photoUrl: editForm.photoUrl,
        address: editForm.address,
        status: org.status === 'rejected' ? 'pending' : org.status,
        verification: {
          ...org.verification,
          ...editForm.verification,
          submittedAt: now,
          lastModifiedAt: now,
          revisionCount: newRevCount,
          isRevisedAfterSubmission: true,
          ...(org.status === 'rejected' ? { rejectionReason: '' } : {})
        },
        updatedAt: now
      });
      setIsSaving(false);
      setIsEditing(false);
      setSuccessMessage(`Application updated to Revision #${newRevCount}. Submission timestamp refreshed for Trust & Safety review.`);
      setTimeout(() => setSuccessMessage(''), 6000);
    } catch (err: any) {
      setIsSaving(false);
      setErrorMessage(err?.message || 'Failed to update organization details.');
    }
  };

  const selectedCountry = COUNTRIES_STATES.find(c => c.name === editForm.address.country);
  const availableStates = selectedCountry ? selectedCountry.states : [];

  const formatTimestamp = (dateVal: any) => {
    if (!dateVal) return null;
    try {
      const d = dateVal?.toDate ? dateVal.toDate() : new Date(dateVal);
      return isNaN(d.getTime()) ? null : d.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return null;
    }
  };

  const submittedDateStr = formatTimestamp(org.verification?.submittedAt || org.createdAt);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col justify-between">
      {/* Top Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30 px-6 py-3.5 shadow-xs">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            {org.photoUrl ? (
              <img src={org.photoUrl} alt={org.name} className="w-10 h-10 rounded-xl object-cover border border-slate-200 shadow-xs" />
            ) : (
              <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center font-bold text-lg">
                {org.name.charAt(0).toUpperCase()}
              </div>
            )}
            <div>
              <h1 className="text-base sm:text-lg font-bold text-slate-900 leading-tight">{org.name}</h1>
              <p className="text-xs text-slate-500 capitalize">{org.type} • EduGravity Onboarding</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* User Profile Button / Badge with Edit trigger */}
            <button
              onClick={() => setIsEditingProfile(true)}
              className="flex items-center gap-2.5 px-3 py-1.5 rounded-xl hover:bg-slate-100 border border-transparent hover:border-slate-200 transition-all text-left group"
              title="Click to edit your profile info"
            >
              <div className="hidden sm:block text-right">
                <div className="flex items-center gap-1 justify-end">
                  <p className="text-xs font-bold text-slate-800 group-hover:text-indigo-600 transition-colors">{user.name}</p>
                  <span className="material-icons-round text-slate-400 group-hover:text-indigo-600 text-xs">edit</span>
                </div>
                <p className="text-[11px] text-slate-500">{user.email}</p>
              </div>
              <div className="relative">
                <Avatar photoUrl={user.photoUrl} name={user.name} size="sm" />
                <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-indigo-600 text-white rounded-full flex items-center justify-center text-[9px] shadow-xs">
                  <span className="material-icons-round text-[9px]">edit</span>
                </span>
              </div>
            </button>

            <button
              onClick={onLogout}
              className="px-3.5 py-1.5 text-xs font-semibold text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors flex items-center gap-1.5"
            >
              <span className="material-icons-round text-base">logout</span>
              <span className="hidden sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content Area */}
      <main className="max-w-3xl w-full mx-auto px-4 py-8 flex-1">
        {successMessage && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-2xl flex items-center gap-3 animate-fade-in shadow-xs">
            <span className="material-icons-round text-emerald-600">check_circle</span>
            <p className="text-sm font-medium">{successMessage}</p>
          </div>
        )}

        {status === 'pending' && (
          <div className="space-y-6">
            {/* Primary Status Banner */}
            <div className="bg-gradient-to-br from-indigo-900 via-indigo-800 to-slate-900 text-white rounded-3xl p-6 sm:p-8 shadow-xl relative overflow-hidden">
              <div className="absolute right-0 top-0 translate-x-10 -translate-y-10 w-64 h-64 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />
              
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/20 text-amber-300 text-xs font-bold uppercase tracking-wider border border-amber-400/30">
                  <span className="w-2 h-2 rounded-full bg-amber-400 animate-ping" />
                  Verification Under Review
                </div>
                <div className="flex flex-wrap items-center gap-2 text-xs text-indigo-200 font-medium">
                  {isRevised && (
                    <span className="px-2.5 py-0.5 rounded-full bg-indigo-500/30 text-indigo-200 border border-indigo-400/30 font-semibold text-[11px]">
                      Revision #{revisionCount}
                    </span>
                  )}
                  {submittedDateStr && (
                    <span>Last Updated: <strong className="text-white">{submittedDateStr}</strong></span>
                  )}
                </div>
              </div>

              <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">
                We're Verifying Your Institution
              </h2>
              <p className="text-indigo-100 text-sm leading-relaxed max-w-xl">
                To protect student privacy and ensure a secure, anti-spam environment, our Trust &amp; Safety team verifies every new school and coaching center before unlocking teacher invitations and parent portals.
              </p>

              {/* Progress Timeline */}
              <div className="mt-8 pt-6 border-t border-indigo-700/50 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0 shadow-md">
                    <span className="material-icons-round text-lg">check</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white">1. Registered</p>
                    <p className="text-[11px] text-emerald-300">
                      {isRevised ? `Rev #${revisionCount} submitted` : 'Details submitted'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-400 text-slate-950 flex items-center justify-center flex-shrink-0 font-bold shadow-md animate-pulse">
                    <span className="material-icons-round text-lg">search</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white">2. Trust Review</p>
                    <p className="text-[11px] text-amber-300">Review in progress</p>
                  </div>
                </div>

                <div className="flex items-center gap-3 opacity-60">
                  <div className="w-8 h-8 rounded-full bg-indigo-950/80 border border-indigo-600 text-indigo-300 flex items-center justify-center flex-shrink-0 font-bold">
                    <span className="material-icons-round text-base">lock_open</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-white">3. Platform Ready</p>
                    <p className="text-[11px] text-indigo-300">Instant activation</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Live Synchronizer & Auto-Reset Notice */}
            <div className="bg-indigo-50/70 border border-indigo-100 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="material-icons-round text-indigo-600 animate-spin text-xl flex-shrink-0">sync</span>
                <div>
                  <p className="text-xs font-bold text-indigo-900">Live Auto-Sync &amp; Instant Review Update</p>
                  <p className="text-[11px] text-indigo-700">
                    This screen automatically unlocks once approved. If you edit any institution info below, your submission is immediately refreshed for review.
                  </p>
                </div>
              </div>
              <span className="text-xs px-2.5 py-1 bg-white border border-indigo-200 text-indigo-800 font-semibold rounded-lg shadow-2xs whitespace-nowrap self-start sm:self-auto">
                Auto-Reset Active
              </span>
            </div>

            {/* Submitted Application Dossier Card */}
            <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-base font-bold text-slate-900">Submitted Institution Details</h3>
                    {isRevised && (
                      <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded text-[10px] font-bold">
                        Revision #{revisionCount}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">
                    {submittedDateStr ? `Last submitted on ${submittedDateStr}` : 'Information provided for verification'}
                  </p>
                </div>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-3 py-1.5 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-xl transition-colors flex items-center gap-1.5"
                >
                  <span className="material-icons-round text-sm">edit</span>
                  <span>Edit Details</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-slate-400 block mb-1 uppercase font-semibold text-[10px]">Institution Name</span>
                  <span className="font-bold text-slate-800 text-sm">{org.name}</span>
                </div>

                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-slate-400 block mb-1 uppercase font-semibold text-[10px]">Category &amp; Type</span>
                  <span className="font-bold text-slate-800 capitalize text-sm">{org.type}</span>
                </div>

                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-slate-400 block mb-1 uppercase font-semibold text-[10px]">Contact Phone</span>
                  <span className="font-semibold text-slate-800">{org.phone || 'Not provided'}</span>
                </div>

                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-slate-400 block mb-1 uppercase font-semibold text-[10px]">Location</span>
                  <span className="font-semibold text-slate-800">
                    {[org.address?.street, org.address?.city, org.address?.state, org.address?.country].filter(Boolean).join(', ') || 'Not provided'}
                  </span>
                </div>

                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-slate-400 block mb-1 uppercase font-semibold text-[10px]">Affiliation / Reg Number</span>
                  <span className="font-semibold text-slate-800 font-mono">
                    {org.verification?.affiliationNumber || <span className="text-slate-400 italic">None provided</span>}
                  </span>
                </div>

                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                  <span className="text-slate-400 block mb-1 uppercase font-semibold text-[10px]">Website / Maps / Social</span>
                  {org.verification?.publicUrl ? (
                    <a
                      href={org.verification.publicUrl.startsWith('http') ? org.verification.publicUrl : `https://${org.verification.publicUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-indigo-600 font-semibold underline truncate block hover:text-indigo-800"
                    >
                      {org.verification.publicUrl}
                    </a>
                  ) : (
                    <span className="text-slate-400 italic">None provided</span>
                  )}
                </div>

                <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100 sm:col-span-2">
                  <span className="text-slate-400 block mb-1 uppercase font-semibold text-[10px]">Verification Document / Letterhead</span>
                  {org.verification?.documentUrl ? (
                    <div className="flex items-center justify-between mt-1">
                      <div className="flex items-center gap-2">
                        <span className="material-icons-round text-indigo-600">description</span>
                        <span className="font-semibold text-slate-800">{org.verification.documentName || 'Verification_Document.jpg'}</span>
                      </div>
                      <button
                        onClick={() => setShowDocModal(true)}
                        className="px-2.5 py-1 bg-white border border-slate-200 text-slate-700 hover:bg-slate-100 font-medium rounded-lg text-xs"
                      >
                        Preview
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 italic">No document uploaded yet</span>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="text-indigo-600 hover:underline text-xs font-semibold"
                      >
                        + Upload Letterhead / Certificate
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Need Fast-Track Help Box */}
            <div className="bg-slate-100/80 border border-slate-200 rounded-2xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h4 className="text-sm font-bold text-slate-800">Need Fast-Track Verification?</h4>
                <p className="text-xs text-slate-500">If your academic session starts soon, contact our onboarding desk.</p>
              </div>
              <div className="flex items-center gap-3 w-full sm:w-auto">
                <a
                  href={`mailto:support@edugravity.com?subject=Expedited%20Verification%20for%20${encodeURIComponent(org.name)}&body=Hello%20EduGravity%20Team,%0A%0APlease%20expedite%20verification%20for%20our%20institution:%0AName:%20${encodeURIComponent(org.name)}%0AOrg%20ID:%20${org.id}%0APhone:%20${encodeURIComponent(org.phone || '')}`}
                  className="flex-1 sm:flex-none px-4 py-2 bg-white border border-slate-300 text-slate-700 font-semibold rounded-xl text-xs hover:bg-slate-50 transition-colors text-center flex items-center justify-center gap-1.5"
                >
                  <span className="material-icons-round text-sm">email</span>
                  <span>Email Support</span>
                </a>
                <a
                  href={`https://wa.me/15551234567?text=Hi%20EduGravity%20Team,%20we%20submitted%20verification%20for%20${encodeURIComponent(org.name)}%20(Org%20ID:%20${org.id})`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 sm:flex-none px-4 py-2 bg-emerald-600 text-white font-semibold rounded-xl text-xs hover:bg-emerald-700 transition-colors text-center flex items-center justify-center gap-1.5 shadow-xs"
                >
                  <span className="material-icons-round text-sm">chat</span>
                  <span>WhatsApp Desk</span>
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Rejected State */}
        {status === 'rejected' && (
          <div className="bg-white border border-red-200 rounded-3xl p-8 shadow-md text-center max-w-xl mx-auto space-y-6">
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto text-3xl">
              <span className="material-icons-round">cancel</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Application Requires Attention</h2>
              <p className="text-sm text-slate-600">
                Your institution application could not be verified automatically by our Trust &amp; Safety team.
              </p>
            </div>

            {org.verification?.rejectionReason && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-left">
                <span className="text-xs font-bold text-red-800 uppercase block mb-1">Reason from Reviewer:</span>
                <p className="text-sm text-red-900 font-medium">{org.verification.rejectionReason}</p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-sm transition-colors shadow-xs"
              >
                Update &amp; Re-submit Information
              </button>
              <a
                href="mailto:support@edugravity.com?subject=Appeal%20Verification%20Rejection"
                className="px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl text-sm transition-colors"
              >
                Appeal to Trust Team
              </a>
            </div>
          </div>
        )}

        {/* Suspended State */}
        {status === 'suspended' && (
          <div className="bg-white border border-amber-200 rounded-3xl p-8 shadow-md text-center max-w-xl mx-auto space-y-6">
            <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto text-3xl">
              <span className="material-icons-round">pause_circle</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Account Temporarily Paused</h2>
              <p className="text-sm text-slate-600">
                Access to this organization has been paused by the platform administrators. Please contact your account manager for reactivation.
              </p>
            </div>

            <a
              href="mailto:support@edugravity.com?subject=Reactivation%20Request"
              className="inline-block px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-sm transition-colors shadow-xs"
            >
              Contact Support
            </a>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-xs text-slate-400 border-t border-slate-100">
        EduGravity Trust &amp; Safety Compliance • Secure Anti-Spam Educational Architecture
      </footer>

      {/* Edit Details Modal */}
      {isEditing && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in overflow-y-auto">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full shadow-2xl border border-slate-100 my-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-xl font-bold text-slate-900">Edit Institution Details</h3>
                <p className="text-xs text-slate-500">
                  Updates will reset submission timestamp (Option B Auto-Reset) for reviewer
                </p>
              </div>
              <button
                onClick={() => setIsEditing(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center"
              >
                <span className="material-icons-round text-lg">close</span>
              </button>
            </div>

            {errorMessage && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-xs font-semibold">
                {errorMessage}
              </div>
            )}

            <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
              <ImageUpload
                onImageUpload={(url) => setEditForm(prev => ({ ...prev, photoUrl: url }))}
                currentImage={editForm.photoUrl}
                label="Institution Logo"
              />

              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Institution Name *</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={e => setEditForm(prev => ({ ...prev, name: e.target.value }))}
                  maxLength={50}
                  className="w-full p-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="e.g. Greenwood Academy"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Category *</label>
                <select
                  value={editForm.type}
                  onChange={e => setEditForm(prev => ({ ...prev, type: e.target.value as any }))}
                  className="w-full p-3 border border-slate-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="school">K-12 School</option>
                  <option value="coaching">Coaching / Tuition Center</option>
                </select>
              </div>

              <PhoneInput
                value={editForm.phone}
                onChange={val => setEditForm(prev => ({ ...prev, phone: val }))}
                label="Organization Phone Number *"
              />

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-1">Country *</label>
                  <select
                    value={editForm.address.country}
                    onChange={e => setEditForm(prev => ({
                      ...prev,
                      address: { ...prev.address, country: e.target.value, state: '' }
                    }))}
                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs"
                  >
                    <option value="" disabled>Select Country</option>
                    {COUNTRIES_STATES.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-1">State / Province *</label>
                  <select
                    value={editForm.address.state}
                    onChange={e => setEditForm(prev => ({
                      ...prev,
                      address: { ...prev.address, state: e.target.value }
                    }))}
                    disabled={!editForm.address.country}
                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs"
                  >
                    <option value="" disabled>Select State</option>
                    {availableStates.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-1">City *</label>
                  <input
                    type="text"
                    value={editForm.address.city}
                    onChange={e => setEditForm(prev => ({
                      ...prev,
                      address: { ...prev.address, city: e.target.value }
                    }))}
                    maxLength={50}
                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs"
                    placeholder="City"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-600 block mb-1">Street Address</label>
                  <input
                    type="text"
                    value={editForm.address.street}
                    onChange={e => setEditForm(prev => ({
                      ...prev,
                      address: { ...prev.address, street: e.target.value }
                    }))}
                    maxLength={100}
                    className="w-full p-2.5 border border-slate-300 rounded-xl text-xs"
                    placeholder="Street / Area"
                  />
                </div>
              </div>

              {/* Verification Specific Fields */}
              <div className="pt-3 border-t border-slate-200">
                <h4 className="text-xs font-bold text-indigo-900 uppercase tracking-wide mb-3">
                  Verification Proof Details
                </h4>

                <div className="space-y-3">
                  <div>
                    <label className="text-xs font-semibold text-slate-700 block mb-1">
                      Affiliation / Registration / Tax Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={editForm.verification.affiliationNumber}
                      onChange={e => setEditForm(prev => ({
                        ...prev,
                        verification: { ...prev.verification, affiliationNumber: e.target.value }
                      }))}
                      maxLength={40}
                      className="w-full p-2.5 border border-slate-300 rounded-xl text-xs font-mono"
                      placeholder="e.g. CBSE/12345 or Society Reg No."
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700 block mb-1">
                      Website / Google Maps / Facebook Link (Optional)
                    </label>
                    <input
                      type="text"
                      value={editForm.verification.publicUrl}
                      onChange={e => setEditForm(prev => ({
                        ...prev,
                        verification: { ...prev.verification, publicUrl: e.target.value }
                      }))}
                      maxLength={150}
                      className="w-full p-2.5 border border-slate-300 rounded-xl text-xs"
                      placeholder="https://..."
                    />
                  </div>

                  <div>
                    <label className="text-xs font-semibold text-slate-700 block mb-1">
                      Letterhead / Certificate / ID Document (Optional)
                    </label>
                    <label className="flex items-center justify-center gap-2 p-3 border-2 border-dashed border-slate-300 hover:border-indigo-500 rounded-xl cursor-pointer bg-slate-50 hover:bg-indigo-50/50 transition-colors">
                      <span className="material-icons-round text-indigo-600 text-lg">upload_file</span>
                      <span className="text-xs font-medium text-slate-700">
                        {editForm.verification.documentName || 'Upload Photo or PDF (Max 5MB)'}
                      </span>
                      <input type="file" accept="image/*,application/pdf" onChange={handleDocUpload} className="hidden" />
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-6 border-t border-slate-100 mt-6">
              <button
                onClick={() => setIsEditing(false)}
                disabled={isSaving}
                className="flex-1 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={isSaving}
                className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold rounded-xl text-xs transition-colors flex items-center justify-center gap-2 shadow-xs disabled:opacity-50"
              >
                {isSaving ? (
                  <>
                    <span className="material-icons-round animate-spin text-sm">sync</span>
                    <span>Saving &amp; Refreshing Timestamp...</span>
                  </>
                ) : (
                  <span>Save &amp; Update Application</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Profile Edit Modal */}
      {isEditingProfile && (
        <EditProfileModal
          user={user}
          onClose={() => setIsEditingProfile(false)}
          onSave={async (userId, data) => {
            await onUpdateUser(userId, data);
          }}
          t={(key: string) => {
            const map: Record<string, string> = {
              edit_profile: 'Edit Profile',
              your_photo: 'Profile Photo',
              your_full_name: 'Full Name',
              phone_number: 'Mobile Phone Number',
              preferred_language: 'Preferred Language',
              cancel: 'Cancel',
              save_changes: 'Save Profile Changes'
            };
            return map[key] || key;
          }}
        />
      )}

      {/* Document Preview Modal */}
      {showDocModal && org.verification?.documentUrl && (
        <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-3xl p-6 max-w-2xl w-full shadow-2xl border border-slate-100 max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="material-icons-round text-indigo-600">verified</span>
                <h3 className="font-bold text-slate-900 text-sm">Verification Document Preview</h3>
              </div>
              <button
                onClick={() => setShowDocModal(false)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center"
              >
                <span className="material-icons-round text-lg">close</span>
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 rounded-2xl p-2 flex items-center justify-center min-h-[300px]">
              {org.verification.documentUrl.startsWith('data:image') || org.verification.documentUrl.includes('firebasestorage') ? (
                <img src={org.verification.documentUrl} alt="Document" className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-xs" />
              ) : (
                <iframe src={org.verification.documentUrl} title="Document PDF" className="w-full h-[60vh] rounded-lg" />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
